import { faBell, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  Progress,
  UncontrolledDropdown,
} from "reactstrap";
import "./Notifications.scss";
import getDataFromApi from "../getDataFromApi";
import ErrorScreen from "../error/ErrorScreen";
import { StyledTooltip } from "../../views/project/label/annotation/EditorView/EditorTopNavigationBar/EditorTopNavigationBar";
import AlertComponent from "../alert/AlertComponent";
import { BellIcon, CheckCircleIcon, CloseIcon, FailedIcon, PauseIcon, QueuedIcon, RoundedClockIcon, TerminatedIcon, TransferTitleIcon, TrashIcon, UpdateIcon } from "../../views/Common/SvgIcons/WorkflowIcons";
import { getCokkie } from "../../views/login/Login";
import { CheckIcon } from "lucide-react";


export default function Notifications() {
  const [data, setData] = useState({
    unread_count: 0,
    notifications: [],
  });
  const [alert, setAlert] = useState();
  const workerRef = useRef(null);

  const workerMode = "real"; // "fake" | "real"

  function isNotificationRead(notification) {
    const read = typeof notification === "object" ? notification?.read : notification;
    return read === true || String(read).toLowerCase() === "true";
  }

  function normalizeNotification(notification) {
    if (!notification) return null;

    return {
      ...notification,
      read: isNotificationRead(notification) ? "True" : "False",
    };
  }

  function orderNotifications(notifications) {
    // Keep the stream/API order; process notifications should not jump ahead of newer items.
    return [...notifications];
  }

  function getUnreadCount(notifications, unreadCount) {
    const parsedUnreadCount = Number(unreadCount);

    if (Number.isFinite(parsedUnreadCount)) {
      return parsedUnreadCount;
    }

    return notifications.filter((n) => !isNotificationRead(n)).length;
  }

  // function getNotificationTitle(message = "") {
  //   const markedTitle = message.match(/(\*\*|_)(.*?)(\*\*|_)/)?.[2];
  //   if (markedTitle) return markedTitle;

  //   return message.match(/\b(complete|completed|created|queued|failed|terminated|transfer|deleted|paused|updated)\b/i)?.[1] || "Notification";
  // }

  function getNotificationTitle(message = "") {
  const markedTitle = message.match(/(\*\*|_)(.*?)(\*\*|_)/)?.[2];
  if (markedTitle) return markedTitle;

  const matches = [...message.matchAll(
    /\b(complete|completed|created|queued|failed|terminated|transfer|deleted|paused|updated)\b/gi
  )];

  const title = matches.length
    ? matches[matches.length - 1][1]
    : "Notification";

  return title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();
}

  function getNotificationMessage(message = "") {
    return message.replace(/(\*\*|_)(.*?)(\*\*|_)/, "").trim();
  }

  function detectNewNotification(notif) {
    if (!notif?.created_on) return;

    if (isNotificationJustNow(notif.created_on)) {
      const timeElapsed = time_ago(notif.created_on);
      const messageWithTime = `${notif.message} ${timeElapsed}`;
      setAlert({ message: messageWithTime });
    }
  }

  function mergeNotification(newNotif) {
    const normalizedNotif = normalizeNotification(newNotif);
    if (!normalizedNotif) return;

    setData((prev) => {
      const existing = prev.notifications || [];
      const alreadyExists = existing.some((n) => String(n.id) === String(normalizedNotif.id));

      const updatedNotifications = alreadyExists
        ? existing.map((n) => (String(n.id) === String(normalizedNotif.id) ? { ...n, ...normalizedNotif } : n))
        : [normalizedNotif, ...existing];

      const orderedNotifications = orderNotifications(updatedNotifications);
      const unreadCount = getUnreadCount(orderedNotifications);

      return {
        ...prev,
        unread_count: unreadCount,
        notifications: orderedNotifications,
      };
    });
  }

  function replaceAllNotifications(result) {
    const notifications = (result?.notifications || [])
      .map(normalizeNotification)
      .filter(Boolean);
    const orderedNotifications = orderNotifications(notifications);

    setData({
      unread_count: getUnreadCount(orderedNotifications, result?.unread_count),
      notifications: orderedNotifications,
    });
  }

  function isNotificationJustNow(createdOn) {
    const createdDate = new Date(createdOn.includes("Z") ? createdOn : `${createdOn}Z`);
    const currentTime = new Date();
    const timeDiffSeconds = (currentTime.getTime() - createdDate.getTime()) / 1000;
    return timeDiffSeconds >= 0 && timeDiffSeconds < 10;
  }

  function time_ago(time) {
    switch (typeof time) {
      case "number":
        break;
      case "string":
        time = +new Date(time.includes("Z") ? time : time + "Z");
        break;
      case "object":
        if (time.constructor === Date) time = time.getTime();
        break;
      default:
        time = +new Date();
    }

    var time_formats = [
      [60, "s ago", 1],
      [3600, "m ago", 60],
      [86400, "H ago", 3600],
      [604800, "D ago", 86400],
      [2419200, "W ago", 604800],
      [29030400, "M ago", 2419200],
      [2903040000, "Y ago", 29030400],
      [5806080000, "Last century", "Next century"],
      [58060800000, "centuries", 2903040000],
    ];

    var seconds = (+new Date() - time) / 1000,
      token = "",
      list_choice = 1;

    if (seconds === 0) return "Just now";
    if (seconds < 0) {
      seconds = Math.abs(seconds);
      token = "from now";
      list_choice = 2;
    }

    var i = 0,
      format;

    while ((format = time_formats[i++])) {
      if (seconds < format[0]) {
        if (typeof format[2] === "string") return format[list_choice];
        return Math.floor(seconds / format[2]) + "" + format[1] + "" + token;
      }
    }

    return time;
  }

  async function markOneRead(id) {
    try {
      await getDataFromApi(`${window["ENV"].API_URL}/notifications/?notification_id=${id}`, "post");

      setData((prev) => ({
        ...prev,
        notifications: prev.notifications.map((n) =>
          String(n.id) === String(id) ? { ...n, read: "True" } : n
        ),
        unread_count: Math.max(
          0,
          prev.notifications.filter((n) => !isNotificationRead(n) && String(n.id) !== String(id)).length
        ),
      }));
    } catch (e) {
      console.log(e.message);
    }
  }

  async function markAllRead() {
    try {
      await getDataFromApi(`${window["ENV"].API_URL}/notifications/`, "post");

      setData((prev) => ({
        ...prev,
        unread_count: 0,
        notifications: prev.notifications.map((n) => ({ ...n, read: "True" })),
      }));
    } catch (e) {
      console.log(e.message);
    }
  }

  async function clearAll() {
    try {
      await getDataFromApi(`${window["ENV"].API_URL}/notifications/`, "delete");

      setData({
        unread_count: 0,
        notifications: [],
      });
    } catch (e) {
      console.log(e.message);
    }
  }

  useEffect(() => {

    const worker =
      workerMode === "real"
        ? new Worker(new URL("../../worker/notificationStream.js", import.meta.url))
        : new Worker(new URL("../../worker/fakeNotificationStream.js", import.meta.url));

    workerRef.current = worker;

    worker.onmessage = (event) => {
      const msg = event.data;

      if (msg.type === "connected") {
        // console.log("Notification stream connected");
        return;
      }

      if (msg.type === "retry") {
        console.log("Notification stream retry:", msg.message, msg.retries);
        return;
      }

      if (msg.type === "fatal") {
        console.log("Notification stream fatal:", msg.message);
        return;
      }

      if (msg.type === "parse_error") {
        console.log("Notification stream parse error:", msg.message, msg.raw);
        return;
      }

      if (msg.type === "snapshot") {
        replaceAllNotifications(msg.data);
        return;
      }

      if (msg.type === "message") {
        const payload = msg.payload;

        if (payload?.kind === "batch") {
          replaceAllNotifications(payload.data);
          return;
        }

        if (payload?.kind === "single" && payload.data) {
          detectNewNotification(payload.data);
          mergeNotification(payload.data);
        }
      }
    };

    worker.postMessage({
      type: "start",
      url: `${window["ENV"].API_URL}/notifications/stream`,
      token: getCokkie("jwtToken"),
    });

    return () => {
      worker.postMessage({ type: "stop" });
      worker.terminate();
    };
  }, []);

  return (
    <>
      <AlertComponent alert={alert} topright={'top'} />
      <UncontrolledDropdown nav navbar="true">
        <DropdownToggle
          nav
          id="notifications"
          onClick={(e) => setData({ ...data, next_ping: 1 })}
        >
          {data?.unread_count > 0 && (
            <span className="notification-badge text-white px-1" style={{ opacity: '9', width: '', lineHeight: 'normal' }}>
              {data?.unread_count}
            </span>
          )}
          <StyledTooltip title='Notifications'>
            {/* <FontAwesomeIcon icon={faBell} style={{ fontSize: "18px", color:'gray'}} /> */}
            <BellIcon className="wf-icon" />
          </StyledTooltip>
        </DropdownToggle>
        <DropdownMenu right className="nav-profile-menu container-card nav-pop-up" style={{ border: 'none', minWidth: 300, marginRight: '-10%', maxWidth: '90%'}}>

          <div className="d-a justify-content-between p-3" style={{ borderBottom: '1px solid var(--glass-color)' }}>
            <h6 className="text-center fw-bold m-0">Notifications</h6>
            <DropdownItem
              className="p-0 border-0 bg-transparent text-end w-auto"
              onClick={(e) => e.stopPropagation()} // optional safety
            >
              <CloseIcon className="wf-icon text-secondary-small text-secondary" />
            </DropdownItem>
          </div>
          {data?.notifications?.length > 0 ? (
            <>
              <div className="p-0">
                {/* <div className="d-flex justify-content-between w-100 p-2">
                  <div
                    className="text-primary   fw-bold cursor-pointer pe-3"
                    onClick={() => fetchData(`notifications/`, "delete")}
                  >
                    Clear all
                  </div>
                </div> */}
                <div
                  className="notify-container ms-0 w-100"
                  style={{ overflowX: "hidden" }}
                >
                  <div className="d-flex flex-wrap pt-3 w-100">
                    {data.notifications.map((v, index) => {
                      const title = getNotificationTitle(v.message);
                      const titleType = title.toLowerCase();
                      const isRead = isNotificationRead(v);
                      return (
                        <div className={`notification-item d-flex align-items-start gap-3 px-3 mb-2 pb-2 pt-2 w-100 ${isRead ? "notification-item--read" : ""}`} key={v.id ?? index}
                          style={{
                            whiteSpace: "pre-wrap",
                            overflow: "hidden",
                            userSelect: "none",
                            borderBottom: "1px solid var(--glass-color)"
                          }}>
                          {
                            (titleType === 'complete' || titleType === 'completed' || titleType === 'created') ? (
                              <CheckIcon className="text-primary" style={{ width: '17px', height: '17px' }} />
                            ) : (titleType === 'queued') ? (
                              <QueuedIcon className="text-gray-400" style={{ width: '17px', height: '17px' }} />
                            ) : (titleType === 'failed') ? (
                              <FailedIcon className="text-danger" style={{ width: '17px', height: '17px' }} />
                            ) : (titleType === 'terminated') ? (
                              <TerminatedIcon className="text-danger" style={{ width: '17px', height: '17px' }} />
                            ) : (titleType === 'transfer') ? (
                              <TransferTitleIcon className="text-blue-500" style={{ width: '17px', height: '17px' }} />
                            ) : (titleType === 'update' || titleType === 'updated') ? (
                              <UpdateIcon className="text-green-500" style={{ width: '17px', height: '17px' }} />
                            ) : (titleType === 'deleted') ? (
                              <TrashIcon className="text-danger" style={{ width: '17px', height: '17px' }} />
                            ) : (titleType === 'paused') ? (
                              <PauseIcon className="text-warning" style={{ width: '17px', height: '17px' }} />
                            ) :
                            (
                              <span style={{ width: '17px', height: '17px' }}></span>
                            )
                          }
                          <div className="w-100"
                            // onClick={() => {
                            //   fetchData(`notifications/?notification_id=${v.id}`, "post");
                            //   setData(prev => ({ ...prev, next_ping: 1 })); // 👈 force refetch
                            // }}
                            onClick={() => {
                              if (workerMode === "fake") {
                                workerRef.current?.postMessage({ type: "markOneRead", id: v.id });
                              } else {
                                markOneRead(v.id);
                              }
                            }}

                          >
                            <div className="d-a justify-content-between w-100">
                              <h6 className="mb-0" style={{ lineHeight: 'normal', fontSize: '90%' }}>{title.charAt(0).toUpperCase() + title.slice(1)}</h6>
                              <span style={{ lineHeight: 'normal', fontSize: '75%' }}><RoundedClockIcon className="wf-icon-small text" /> {time_ago(v.created_on)}</span>
                            </div>
                            <span className="notification-message" style={{ fontSize: '80%', lineHeight: '1.1' }}>{getNotificationMessage(v.message)}</span>
                            {v.progress > 0 && v.progress < 100 &&<Progress value={v.progress} animated>
                              {v.progress}
                            </Progress>}
                          </div>

                          {/* <p className="d-flex">
                        {v.type === "event" ? (
                          <div
                            style={{ flexGrow: 1 }}
                            className="cursor-pointer my-2 p-3 h6 d-flex justify-content-between align-items-center grey-card"
                            onClick={() =>
                              fetchData(
                                `notifications/?notification_id=${v.id}`,
                                "post"
                              )
                            }
                          >

                            <FontAwesomeIcon
                              className="ms-2 cursor-pointer"
                              icon={faTimes}
                              onClick={(e) => {
                                e.preventDefault();
                                fetchData(
                                  `notifications/?notification_id=${v.id}`,
                                  "delete"
                                );
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className="my-2 p-3 h6 grey-card"
                            style={{
                              width: "400px",
                              whiteSpace: "pre-wrap",
                              overflow: "hidden",
                              flexGrow: 1,
                            }}
                          >
                            <div className="pb-2">{v.message}</div>
                            <Progress value={v.progress} animated>
                              {v.progress}
                            </Progress>
                          </div>
                        )}
                      </p> */}
                        </div>
                      )
                    })}
                  </div>

                </div>
                <div className="d-a justify-content-between p-3">
                  {data?.notifications?.length > 0 &&
                    <button
                      style={{ fontSize: '90%' }}
                      className="text-primary fw-bold cursor-pointer p-0 border-0"
                      onClick={() => {
                        if (workerMode === "fake") {
                          workerRef.current?.postMessage({ type: "markAllRead" });
                        } else {
                          markAllRead();
                        }
                      }}
                    >
                      Read all
                    </button>}
                  <button className="plian-button text-danger p-0 border-0" onClick={() => {
                    if (workerMode === "fake") {
                      workerRef.current?.postMessage({ type: "clearAll" });
                    } else {
                      clearAll();
                    }
                  }} style={{ fontSize: '80%', lineHeight: '1.1' }}> Clear all</button>
                </div>
              </div>
            </>
          ) : (
            <div className="d-j-a p-3 text-secondary">
              All Notifications Are Read
            </div>
          )}
        </DropdownMenu>
      </UncontrolledDropdown>

    </>
    // </Nav>
  );
}
